import { useLayoutEffect, useSyncExternalStore } from 'react';

import { notifyRouteMetadataReady } from '@shared/lib/routeMetadata';

import { absoluteUrl, getSocialImage, NOT_FOUND, schemaJson, type SeoPage } from './metadata';
import { getStaticSeoPage } from './staticPages';
import { router } from '../router';

const subscribe = (notify: () => void) => router.subscribe(notify);
const snapshot = () => router.state.location.pathname;

const setMeta = (attribute: 'name' | 'property', key: string, content?: string) => {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (content === undefined) {
    existing?.remove();
    return;
  }
  const element = existing ?? document.createElement('meta');
  element.setAttribute(attribute, key);
  element.content = content;
  if (!existing) document.head.append(element);
};

const applyMetadata = (page: SeoPage, pathname: string) => {
  const image = getSocialImage(page);
  document.title = page.title;
  setMeta('name', 'description', page.description);
  setMeta(
    'name',
    'robots',
    page.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large',
  );
  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:site_name', 'РПСО');
  setMeta('property', 'og:locale', 'ru_RU');
  setMeta('property', 'og:title', page.title);
  setMeta('property', 'og:description', page.description);
  setMeta('property', 'og:url', absoluteUrl(page.path));
  setMeta('property', 'og:image', image.url);
  setMeta('property', 'og:image:secure_url', image.url);
  setMeta('property', 'og:image:type', image.type);
  setMeta('property', 'og:image:width', image.width);
  setMeta('property', 'og:image:height', image.height);
  setMeta('property', 'og:image:alt', page.title);
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', page.title);
  setMeta('name', 'twitter:description', page.description);
  setMeta('name', 'twitter:image', image.url);
  setMeta('name', 'twitter:image:alt', page.title);
  const canonical =
    document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]') ??
    document.createElement('link');
  canonical.rel = 'canonical';
  canonical.href = absoluteUrl(page.path);
  if (!canonical.isConnected) document.head.append(canonical);
  const schema = document.getElementById('page-schema') ?? document.createElement('script');
  schema.id = 'page-schema';
  schema.setAttribute('type', 'application/ld+json');
  schema.textContent = schemaJson(page);
  if (!schema.isConnected) document.head.append(schema);
  notifyRouteMetadataReady(pathname);
};

/**
 * Метаданные страницы при SPA-переходах. Хук, а не компонент: соседний с RouterProvider элемент
 * разветвлял дерево useId, id на клиенте расходились с пререндером (подписи полей теряли связь)
 */
export const useRouteMetadata = (): void => {
  const pathname = useSyncExternalStore(subscribe, snapshot, snapshot);
  useLayoutEffect(() => {
    if (!pathname.startsWith('/catalog/')) {
      applyMetadata(getStaticSeoPage(pathname) ?? NOT_FOUND, pathname);
      return;
    }
    // Каталог подгружает метаданные и данные только своего раздела. При быстрых переходах
    // результат старого запроса не перезаписывает текущую страницу.
    let cancelled = false;
    void import('./loadCatalogPage')
      .then(({ loadCatalogSeoPage }) => loadCatalogSeoPage(pathname))
      .then((page) => {
        if (!cancelled && pathname === snapshot()) applyMetadata(page, pathname);
      })
      .catch(() => {
        // При ошибке чанка сохраняем метаданные исходного HTML; vite:preloadError обрабатывает main.
      });
    return () => {
      cancelled = true;
    };
  }, [pathname]);
};
